/**
 * South African town/city → province lookup.
 *
 * Used by the CV builder so that a candidate who only says "I'm from Benoni"
 * still gets a complete, recruiter-friendly location line
 * ("Benoni, Gauteng, South Africa") without typing it manually.
 *
 * Pure data + pure functions — safe to import on the client and the server.
 */

export const SA_PROVINCES = [
  "Gauteng",
  "Western Cape",
  "Eastern Cape",
  "KwaZulu-Natal",
  "Free State",
  "Mpumalanga",
  "Limpopo",
  "North West",
  "Northern Cape",
] as const;

export type SaProvince = (typeof SA_PROVINCES)[number];

/** Town / city / township / suburb → province. Keys are lower-case. */
const TOWN_PROVINCE: Record<string, SaProvince> = {
  // ---- Gauteng ----
  johannesburg: "Gauteng",
  joburg: "Gauteng",
  jhb: "Gauteng",
  egoli: "Gauteng",
  sandton: "Gauteng",
  randburg: "Gauteng",
  roodepoort: "Gauteng",
  soweto: "Gauteng",
  midrand: "Gauteng",
  centurion: "Gauteng",
  pretoria: "Gauteng",
  tshwane: "Gauteng",
  benoni: "Gauteng",
  boksburg: "Gauteng",
  brakpan: "Gauteng",
  springs: "Gauteng",
  kempton: "Gauteng",
  "kempton park": "Gauteng",
  edenvale: "Gauteng",
  germiston: "Gauteng",
  alberton: "Gauteng",
  vereeniging: "Gauteng",
  vanderbijlpark: "Gauteng",
  sebokeng: "Gauteng",
  evaton: "Gauteng",
  katlehong: "Gauteng",
  tembisa: "Gauteng",
  daveyton: "Gauteng",
  tsakane: "Gauteng",
  duduza: "Gauteng",
  kwathema: "Gauteng",
  "kwa-thema": "Gauteng",
  vosloorus: "Gauteng",
  thokoza: "Gauteng",
  alexandra: "Gauteng",
  diepsloot: "Gauteng",
  ivory: "Gauteng",
  "ivory park": "Gauteng",
  mamelodi: "Gauteng",
  atteridgeville: "Gauteng",
  soshanguve: "Gauteng",
  garankuwa: "Gauteng",
  "ga-rankuwa": "Gauteng",
  hammanskraal: "Gauteng",
  bronkhorstspruit: "Gauteng",
  krugersdorp: "Gauteng",
  kagiso: "Gauteng",
  randfontein: "Gauteng",
  westonaria: "Gauteng",
  carletonville: "Gauteng",
  heidelberg: "Gauteng",
  nigel: "Gauteng",
  fourways: "Gauteng",
  rosebank: "Gauteng",
  braamfontein: "Gauteng",
  "sandton city": "Gauteng",

  // ---- Western Cape ----
  "cape town": "Western Cape",
  kaapstad: "Western Cape",
  bellville: "Western Cape",
  durbanville: "Western Cape",
  stellenbosch: "Western Cape",
  paarl: "Western Cape",
  wellington: "Western Cape",
  worcester: "Western Cape",
  somerset: "Western Cape",
  "somerset west": "Western Cape",
  khayelitsha: "Western Cape",
  mitchells: "Western Cape",
  "mitchells plain": "Western Cape",
  gugulethu: "Western Cape",
  langa: "Western Cape",
  nyanga: "Western Cape",
  delft: "Western Cape",
  goodwood: "Western Cape",
  parow: "Western Cape",
  claremont: "Western Cape",
  wynberg: "Western Cape",
  "george": "Western Cape",
  knysna: "Western Cape",
  mosselbaai: "Western Cape",
  "mossel bay": "Western Cape",
  oudtshoorn: "Western Cape",
  hermanus: "Western Cape",
  malmesbury: "Western Cape",
  saldanha: "Western Cape",
  vredenburg: "Western Cape",
  swellendam: "Western Cape",
  beaufort: "Western Cape",
  "beaufort west": "Western Cape",

  // ---- Eastern Cape ----
  "gqeberha": "Eastern Cape",
  "port elizabeth": "Eastern Cape",
  pe: "Eastern Cape",
  "east london": "Eastern Cape",
  mthatha: "Eastern Cape",
  umtata: "Eastern Cape",
  bhisho: "Eastern Cape",
  "king williams town": "Eastern Cape",
  "king william's town": "Eastern Cape",
  qonce: "Eastern Cape",
  queenstown: "Eastern Cape",
  komani: "Eastern Cape",
  makhanda: "Eastern Cape",
  grahamstown: "Eastern Cape",
  uitenhage: "Eastern Cape",
  kariega: "Eastern Cape",
  butterworth: "Eastern Cape",
  lusikisiki: "Eastern Cape",
  bizana: "Eastern Cape",
  cradock: "Eastern Cape",
  graaff: "Eastern Cape",
  "graaff-reinet": "Eastern Cape",
  jeffreys: "Eastern Cape",
  "jeffreys bay": "Eastern Cape",

  // ---- KwaZulu-Natal ----
  durban: "KwaZulu-Natal",
  ethekwini: "KwaZulu-Natal",
  ethekweni: "KwaZulu-Natal",
  pinetown: "KwaZulu-Natal",
  umlazi: "KwaZulu-Natal",
  kwamashu: "KwaZulu-Natal",
  chatsworth: "KwaZulu-Natal",
  phoenix: "KwaZulu-Natal",
  verulam: "KwaZulu-Natal",
  tongaat: "KwaZulu-Natal",
  ballito: "KwaZulu-Natal",
  amanzimtoti: "KwaZulu-Natal",
  pietermaritzburg: "KwaZulu-Natal",
  pmb: "KwaZulu-Natal",
  msunduzi: "KwaZulu-Natal",
  newcastle: "KwaZulu-Natal",
  ladysmith: "KwaZulu-Natal",
  richards: "KwaZulu-Natal",
  "richards bay": "KwaZulu-Natal",
  empangeni: "KwaZulu-Natal",
  ulundi: "KwaZulu-Natal",
  eshowe: "KwaZulu-Natal",
  estcourt: "KwaZulu-Natal",
  howick: "KwaZulu-Natal",
  margate: "KwaZulu-Natal",
  "port shepstone": "KwaZulu-Natal",
  vryheid: "KwaZulu-Natal",
  dundee: "KwaZulu-Natal",

  // ---- Free State ----
  bloemfontein: "Free State",
  mangaung: "Free State",
  botshabelo: "Free State",
  thaba: "Free State",
  "thaba nchu": "Free State",
  welkom: "Free State",
  virginia: "Free State",
  odendaalsrus: "Free State",
  sasolburg: "Free State",
  kroonstad: "Free State",
  bethlehem: "Free State",
  qwaqwa: "Free State",
  phuthaditjhaba: "Free State",
  harrismith: "Free State",
  parys: "Free State",
  ficksburg: "Free State",

  // ---- Mpumalanga ----
  mbombela: "Mpumalanga",
  nelspruit: "Mpumalanga",
  witbank: "Mpumalanga",
  emalahleni: "Mpumalanga",
  middelburg: "Mpumalanga",
  secunda: "Mpumalanga",
  standerton: "Mpumalanga",
  ermelo: "Mpumalanga",
  barberton: "Mpumalanga",
  komatipoort: "Mpumalanga",
  malelane: "Mpumalanga",
  bushbuckridge: "Mpumalanga",
  hazyview: "Mpumalanga",
  sabie: "Mpumalanga",
  lydenburg: "Mpumalanga",
  mashishing: "Mpumalanga",
  piet: "Mpumalanga",
  "piet retief": "Mpumalanga",
  kwamhlanga: "Mpumalanga",
  siyabuswa: "Mpumalanga",

  // ---- Limpopo ----
  polokwane: "Limpopo",
  pietersburg: "Limpopo",
  seshego: "Limpopo",
  mankweng: "Limpopo",
  tzaneen: "Limpopo",
  giyani: "Limpopo",
  thohoyandou: "Limpopo",
  makhado: "Limpopo",
  louis: "Limpopo",
  "louis trichardt": "Limpopo",
  musina: "Limpopo",
  mokopane: "Limpopo",
  potgietersrus: "Limpopo",
  lephalale: "Limpopo",
  modimolle: "Limpopo",
  bela: "Limpopo",
  "bela-bela": "Limpopo",
  burgersfort: "Limpopo",
  jane: "Limpopo",
  "jane furse": "Limpopo",
  lebowakgomo: "Limpopo",
  phalaborwa: "Limpopo",
  hoedspruit: "Limpopo",
  malamulele: "Limpopo",
  elim: "Limpopo",

  // ---- North West ----
  mahikeng: "North West",
  mafikeng: "North West",
  rustenburg: "North West",
  klerksdorp: "North West",
  potchefstroom: "North West",
  brits: "North West",
  lichtenburg: "North West",
  vryburg: "North West",
  orkney: "North West",
  stilfontein: "North West",
  "jouberton": "North West",
  ikageng: "North West",
  taung: "North West",
  zeerust: "North West",

  // ---- Northern Cape ----
  kimberley: "Northern Cape",
  upington: "Northern Cape",
  springbok: "Northern Cape",
  kuruman: "Northern Cape",
  kathu: "Northern Cape",
  "de aar": "Northern Cape",
  postmasburg: "Northern Cape",
  colesberg: "Northern Cape",
  calvinia: "Northern Cape",
};

function titleCase(s: string) {
  return s
    .split(/\s+/)
    .map((w) =>
      w.length <= 2 && w === w.toLowerCase()
        ? w
        : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");
}

/** Return the province for a South African town/suburb, if we know it. */
export function provinceForTown(town: string): SaProvince | null {
  const key = town
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!key) return null;
  if (TOWN_PROVINCE[key]) return TOWN_PROVINCE[key];
  // Try the first two words, then the first word (e.g. "Benoni North").
  const parts = key.split(" ");
  for (const candidate of [parts.slice(0, 2).join(" "), parts[0]]) {
    if (candidate && TOWN_PROVINCE[candidate]) return TOWN_PROVINCE[candidate];
  }
  return null;
}

/**
 * Normalise a free-form location into "Town, Province, South Africa".
 * Leaves non-SA / unknown locations untouched (beyond trimming).
 */
export function normaliseSaLocation(raw: string | undefined | null): string {
  const input = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!input) return "";

  const cleaned = input.replace(/,?\s*(south africa|rsa|za)\s*$/i, "").trim();
  const segments = cleaned
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!segments.length) return input;

  const town = segments[0];
  const province = provinceForTown(town);

  // Already contains a province name anywhere in the string?
  const stated = SA_PROVINCES.find((p) =>
    new RegExp(`\\b${p.replace(/[-]/g, "[- ]")}\\b`, "i").test(cleaned),
  );

  if (!province && !stated) return input;

  const finalProvince = stated ?? province!;
  const townLabel = titleCase(town);
  if (townLabel.toLowerCase() === finalProvince.toLowerCase())
    return `${finalProvince}, South Africa`;
  return `${townLabel}, ${finalProvince}, South Africa`;
}

/** True when we could enrich the given location with a province. */
export function canEnrichLocation(raw: string | undefined | null): boolean {
  const value = String(raw ?? "").trim();
  if (!value) return false;
  return normaliseSaLocation(value) !== value;
}
